import { useContext, useEffect, useState } from "react";
import { Box, Typography } from "@analyticsMD/quil";
import { css } from "@emotion/css";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import Button from "@mui/material/Button";
import MalnutritionHeader from "./malnutrition-header";
import MalnutritionChipRow from "./malnutrition-chip-row";
import WhyFlaggedSection from "./why-flagged";
import SupportingDataSection from "./supporting-data";
import MalnutritionAPIContext from "../../contexts/MalnutritionAPIContext";
import { useMalnutritionFeedbackMutation } from "../../api/malnutrition/queries";

import type { FC, ReactNode } from "react";
import type {
  MalnutritionFeedback,
  MalnutritionRiskAssessment,
  PatientWeightHistory,
} from "../../types/Malnutrition";
import {
  common,
  grey,
  red,
  green,
  deepPurple,
  purple,
} from "@mui/material/colors";

const FEEDBACK_PURPLE_TEXT = purple[500];

const styles = {
  pageBg: css`
    background: ${deepPurple[50]};
    min-height: 100vh;
  `,
  feedbackCard: css`
    background: ${common.white};
    margin: 0.75rem;
    padding: 0.75rem 0.875rem;
    border-radius: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  `,
  feedbackHeader: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
  feedbackTitleRow: css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
  `,
  feedbackIcon: css`
    background: ${FEEDBACK_PURPLE_TEXT};
    color: ${common.white};
    border-radius: 0.25rem;
    padding: 0.125rem;
  `,
  feedbackPromptRow: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  `,
};

const sxStyles = {
  feedbackTitle: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: FEEDBACK_PURPLE_TEXT,
  },
  feedbackPrompt: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: grey[800],
  },
  feedbackButtonBase: {
    textTransform: "none",
    borderRadius: "1.25rem",
    minWidth: "3rem",
    height: "1.875rem",
    fontWeight: 600,
  },
  feedbackError: {
    fontSize: "0.75rem",
    color: red[700],
  },
};

// Both answers stay outlined pills; the selected one is tinted per the design —
// green for "Yes", red for "No" — rather than a filled variant.
const feedbackButtonSx = (selected: boolean, tone: "yes" | "no") => {
  if (!selected) {
    return {
      ...sxStyles.feedbackButtonBase,
      color: grey[800],
      borderColor: grey[400],
      "&:hover": { borderColor: grey[600], backgroundColor: grey[50] },
    };
  }
  const palette = tone === "yes" ? green : red;
  return {
    ...sxStyles.feedbackButtonBase,
    color: palette[700],
    borderColor: palette[600],
    backgroundColor: palette[50],
    "&:hover": { borderColor: palette[700], backgroundColor: palette[100] },
  };
};

interface FeedbackCardProps {
  // The user's previously recorded answer (read back from the assessment), or
  // null if they haven't answered yet.
  feedback: MalnutritionFeedback | null;
}

const FeedbackCard: FC<FeedbackCardProps> = ({ feedback }) => {
  const { emrPatientID, emrHospitalID } = useContext(MalnutritionAPIContext);
  const feedbackMutation = useMalnutritionFeedbackMutation(
    emrPatientID,
    emrHospitalID
  );

  // Reflect a prior answer, and keep it in sync when the read-back changes
  // (after a successful save refetches, or the card is reused for a new
  // patient). The user can change their answer, so the buttons never lock.
  const storedVote: "yes" | "no" | null = feedback
    ? feedback.is_good_candidate
      ? "yes"
      : "no"
    : null;
  const [submitted, setSubmitted] = useState<"yes" | "no" | null>(storedVote);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    setSubmitted(storedVote);
  }, [storedVote]);

  const handleVote = (vote: "yes" | "no") => () => {
    if (feedbackMutation.isLoading) return;
    const previous = submitted;
    setSubmitted(vote); // optimistic
    setShowError(false);
    feedbackMutation.mutate(vote === "yes", {
      onError: () => {
        setSubmitted(previous);
        setShowError(true);
      },
    });
  };

  return (
    <Box className={styles.feedbackCard}>
      <Box className={styles.feedbackHeader}>
        <Box className={styles.feedbackTitleRow}>
          <PlaylistAddCheckIcon
            className={styles.feedbackIcon}
            sx={{ fontSize: "1rem" }}
          />
          <Typography sx={sxStyles.feedbackTitle}>Help us improve</Typography>
        </Box>
      </Box>
      <Box className={styles.feedbackPromptRow}>
        <Typography sx={sxStyles.feedbackPrompt}>
          Good candidate for assessment?
        </Typography>
        <Box sx={{ display: "flex", gap: "0.5rem" }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleVote("yes")}
            aria-pressed={submitted === "yes"}
            sx={feedbackButtonSx(submitted === "yes", "yes")}
            disabled={feedbackMutation.isLoading}
          >
            Yes
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleVote("no")}
            aria-pressed={submitted === "no"}
            sx={feedbackButtonSx(submitted === "no", "no")}
            disabled={feedbackMutation.isLoading}
          >
            No
          </Button>
        </Box>
      </Box>
      {showError && (
        <Typography sx={sxStyles.feedbackError}>
          Couldn’t save your feedback. Please try again.
        </Typography>
      )}
    </Box>
  );
};

interface MalnutritionSectionsProps {
  // Both are independent and either can be absent: the assessment endpoint
  // returns null when a patient isn't flagged, and recent-weights can exist
  // (or not) regardless of the assessment.
  assessment: MalnutritionRiskAssessment | null;
  weights: PatientWeightHistory | null;
  onClose?: () => void;
  onExpand?: () => void;
  closeButtonCmpt?: ReactNode;
}

const MalnutritionSections: FC<MalnutritionSectionsProps> = ({
  assessment,
  weights,
  onClose,
  onExpand,
  closeButtonCmpt,
}) => (
  <Box className={styles.pageBg}>
    {assessment && (
      <>
        <MalnutritionHeader
          patient={assessment.patient}
          onClose={onClose}
          onExpand={onExpand}
          closeButtonCmpt={closeButtonCmpt}
        />
        <>
          <MalnutritionChipRow
            status={assessment.status}
            edd={assessment.patient.edd}
          />
          {/* Feedback shows for every status (and null) except "following". When
              it's hidden, keep its top spacing so the section below the chips
              isn't cramped. */}
          {assessment.status !== "following" ? (
            <FeedbackCard feedback={assessment.feedback} />
          ) : (
            <Box sx={{ height: "0.75rem" }} />
          )}
          {assessment.status && (
            <WhyFlaggedSection
              criteria={assessment.criteria_met}
              status={assessment.status}
            />
          )}
        </>
      </>
    )}
    {weights && <SupportingDataSection weights={weights} />}
  </Box>
);

export default MalnutritionSections;
